import type { MemberRole } from '../types/organisation';
import { getEntryRoles, type UserOrganisationEntry } from '../models/user/userOrganisations';
import { isSuperAdminUserRole, userHasPlatformOrgAccess } from './platformAdminAccess';

export type RoleDashboardId =
  | 'organisationAdmin'
  | 'moderator'
  | 'facilitator'
  | 'wanderWriter'
  | 'author'
  | 'participant';

export interface RoleDashboard {
  id: RoleDashboardId;
  menuItemId: string;
  labelKey: string;
  ownedLabelKey: string;
  descriptionKey: string;
  ownedDescriptionKey: string;
  path: string;
}

export interface RoleDashboardUserSnapshot {
  userRole?: string | null;
  organisations?: Record<string, UserOrganisationEntry | { roles?: MemberRole[]; role?: MemberRole }>;
  hasAuthorProfile?: boolean;
  platformAdminRole?: string | null;
}

export const ROLE_DASHBOARDS: RoleDashboard[] = [
  {
    id: 'organisationAdmin',
    menuItemId: 'org-dashboard',
    labelKey: 'roleDashboards.organisation',
    ownedLabelKey: 'roleDashboards.organisationYours',
    descriptionKey: 'roleDashboards.organisationPreview',
    ownedDescriptionKey: 'roleDashboards.organisationViewYours',
    path: '/org-dashboard',
  },
  {
    id: 'moderator',
    menuItemId: 'moderator-dashboard',
    labelKey: 'roleDashboards.moderator',
    ownedLabelKey: 'roleDashboards.moderatorYours',
    descriptionKey: 'roleDashboards.moderatorPreview',
    ownedDescriptionKey: 'roleDashboards.moderatorViewYours',
    path: '/moderator-dashboard',
  },
  {
    id: 'facilitator',
    menuItemId: 'facilitator-dashboard',
    labelKey: 'roleDashboards.facilitator',
    ownedLabelKey: 'roleDashboards.facilitatorYours',
    descriptionKey: 'roleDashboards.facilitatorPreview',
    ownedDescriptionKey: 'roleDashboards.facilitatorViewYours',
    path: '/facilitator-dashboard',
  },
  {
    id: 'wanderWriter',
    menuItemId: 'wander-writer-dashboard',
    labelKey: 'roleDashboards.wanderWriter',
    ownedLabelKey: 'roleDashboards.wanderWriterYours',
    descriptionKey: 'roleDashboards.wanderWriterPreview',
    ownedDescriptionKey: 'roleDashboards.wanderWriterViewYours',
    path: '/wander-writer-dashboard',
  },
  {
    id: 'author',
    menuItemId: 'author-dashboard',
    labelKey: 'roleDashboards.author',
    ownedLabelKey: 'roleDashboards.authorYours',
    descriptionKey: 'roleDashboards.authorPreview',
    ownedDescriptionKey: 'roleDashboards.authorViewYours',
    path: '/author-dashboard',
  },
  {
    id: 'participant',
    menuItemId: 'participant-dashboard',
    labelKey: 'roleDashboards.participant',
    ownedLabelKey: 'roleDashboards.participantYours',
    descriptionKey: 'roleDashboards.participantPreview',
    ownedDescriptionKey: 'roleDashboards.participantViewYours',
    path: '/participant-dashboard',
  },
];

export const ROLE_DASHBOARD_PATHS = ROLE_DASHBOARDS.map((dashboard) => dashboard.path);

export const ROLE_DASHBOARDS_SECTION_ID = 'role-dashboards';

const MENU_ITEM_ID_TO_DASHBOARD_ID: Record<string, RoleDashboardId> = Object.fromEntries(
  ROLE_DASHBOARDS.map((dashboard) => [dashboard.menuItemId, dashboard.id])
) as Record<string, RoleDashboardId>;

export function sessionRoleToDashboardId(
  role?: string | null
): RoleDashboardId | 'superAdmin' | undefined {
  switch (role) {
    case 'superAdmin':
    case 'admin':
      return 'superAdmin';
    case 'organisationAdmin':
      return 'organisationAdmin';
    case 'moderator':
      return 'moderator';
    case 'facilitator':
      return 'facilitator';
    case 'wanderWriter':
      return 'wanderWriter';
    case 'author':
      return 'author';
    case 'member':
    case 'participant':
      return 'participant';
    default:
      return undefined;
  }
}

function pathnameMatchesDashboard(pathname: string, path: string): boolean {
  const pathOnly = path.split('?')[0];
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`);
}

export function getHeldRoleDashboardIds(
  user: RoleDashboardUserSnapshot | null | undefined
): RoleDashboardId[] {
  const ids = new Set<RoleDashboardId>();
  if (!user) return [];

  const sessionId = sessionRoleToDashboardId(user.userRole);
  if (sessionId && sessionId !== 'superAdmin') ids.add(sessionId);

  if (user.hasAuthorProfile) ids.add('author');

  if (user.organisations) {
    for (const entry of Object.values(user.organisations)) {
      for (const role of getEntryRoles(entry as UserOrganisationEntry)) {
        const id = sessionRoleToDashboardId(role);
        if (id && id !== 'superAdmin') ids.add(id);
      }
    }
  }

  return ROLE_DASHBOARDS.filter((dashboard) => ids.has(dashboard.id)).map(
    (dashboard) => dashboard.id
  );
}

/**
 * Extra dashboard links for the top nav. Platform admins use the Org Menu
 * instead, including super-admin previews of every role dashboard.
 */
export function getLinkedRoleDashboardsForNav(
  user: RoleDashboardUserSnapshot | null | undefined,
  pathname: string
): RoleDashboard[] {
  if (!user || userHasPlatformOrgAccess(user)) return [];

  const held = new Set(getHeldRoleDashboardIds(user));
  const sessionId = sessionRoleToDashboardId(user.userRole);

  return ROLE_DASHBOARDS.filter((dashboard) => {
    if (!held.has(dashboard.id)) return false;
    if (dashboard.id === sessionId) return false;
    if (pathnameMatchesDashboard(pathname, dashboard.path)) return false;
    return true;
  });
}

export function roleDashboardRequiresOrganisation(path: string): boolean {
  return (
    path === '/org-dashboard' ||
    path === '/moderator-dashboard' ||
    path === '/facilitator-dashboard' ||
    path === '/participant-dashboard'
  );
}

export function userHoldsRoleDashboard(
  user: RoleDashboardUserSnapshot | null | undefined,
  dashboardId: RoleDashboardId
): boolean {
  return getHeldRoleDashboardIds(user).includes(dashboardId);
}

/** Only super admins may open dashboards for roles they do not hold. */
export function userCanPreviewUnheldRoleDashboards(
  user: RoleDashboardUserSnapshot | null | undefined
): boolean {
  return (
    isSuperAdminUserRole(user?.userRole) || isSuperAdminUserRole(user?.platformAdminRole)
  );
}

export function shouldShowRoleDashboardMenuItem(
  user: RoleDashboardUserSnapshot | null | undefined,
  itemId: string
): boolean {
  const dashboardId = MENU_ITEM_ID_TO_DASHBOARD_ID[itemId];
  if (!dashboardId) return true;
  if (userHoldsRoleDashboard(user, dashboardId)) return true;
  return userCanPreviewUnheldRoleDashboards(user);
}

/** Label and description keys for Org Menu role cards: owned vs preview. */
export function getRoleDashboardMenuCopy(
  user: RoleDashboardUserSnapshot | null | undefined,
  itemId: string
): { labelKey: string; descriptionKey: string } | null {
  const dashboardId = MENU_ITEM_ID_TO_DASHBOARD_ID[itemId];
  if (!dashboardId) return null;
  const dashboard = ROLE_DASHBOARDS.find((item) => item.id === dashboardId);
  if (!dashboard) return null;
  const held = userHoldsRoleDashboard(user, dashboardId);
  return {
    labelKey: held ? dashboard.ownedLabelKey : dashboard.labelKey,
    descriptionKey: held ? dashboard.ownedDescriptionKey : dashboard.descriptionKey,
  };
}

export function withRoleDashboardMenuCopy<
  T extends { id: string; label: string; description?: string },
>(
  items: T[],
  user: RoleDashboardUserSnapshot | null | undefined,
  translateFn: (key: string) => string
): T[] {
  return items.map((item) => {
    const copy = getRoleDashboardMenuCopy(user, item.id);
    if (!copy) return item;
    return {
      ...item,
      label: translateFn(copy.labelKey),
      description: translateFn(copy.descriptionKey),
    };
  });
}

export function withRoleDashboardSectionCopy<
  T extends { id: string; label: string; description?: string },
  S extends { id: string; items: T[] },
>(
  sections: S[],
  user: RoleDashboardUserSnapshot | null | undefined,
  translateFn: (key: string) => string
): S[] {
  return sections.flatMap((section) => {
    if (section.id !== ROLE_DASHBOARDS_SECTION_ID) return [section];
    const visibleItems = withRoleDashboardMenuCopy(
      section.items.filter((item) => shouldShowRoleDashboardMenuItem(user, item.id)),
      user,
      translateFn
    );
    if (visibleItems.length === 0) return [];
    return [{ ...section, items: visibleItems }];
  });
}
